import { z as zod } from 'zod';

import {
  FareEstimateFormConfig,
  FormValidationRegex,
} from '../domain/configs/Config';
import { FareEstimateFormErrorMsg } from '../domain/configs/Store.store';
import { ConsentForCallbackConfig } from '@porterin/component-library/src/domain/country/type';

export type RequirementType = 'personal' | 'business';

export const addressSchema = zod.object({
  landmark: zod.string(),
  location: zod.object({
    lat: zod.number(),
    lng: zod.number(),
  }),
});

export const requirementSchema = zod.enum(['personal', 'business'], {
  invalid_type_error: FareEstimateFormErrorMsg.requirement,
  required_error: FareEstimateFormErrorMsg.requirement,
});

const makeConsentSchema = (config: ConsentForCallbackConfig) => {
  if (config?.isRequired) {
    // Required checkbox -> must always be true
    return zod.literal(true);
  } else {
    return zod.boolean().optional();
  }
};

export const makeSchema = (
  mobileRegex: RegExp,
  consentConfig: ConsentForCallbackConfig = {
    shouldShow: false,
    isRequired: false,
  },
  prefixesToRemove?: string[],
) => {
  return zod.object({
    name: zod
      .string({ required_error: FareEstimateFormErrorMsg.name_required })
      .min(FareEstimateFormConfig.required_length, {
        message: FareEstimateFormErrorMsg.name_required,
      })
      .regex(FormValidationRegex.name, {
        message: FareEstimateFormErrorMsg.name_invalid,
      }),
    mobile: zod
      .string({ required_error: FareEstimateFormErrorMsg.mobile_required })
      .min(FareEstimateFormConfig.required_length, {
        message: FareEstimateFormErrorMsg.mobile_required,
      })
      .regex(mobileRegex, {
        message: FareEstimateFormErrorMsg.mobile_invalid,
      })
      .transform(value => {
        const prefix = prefixesToRemove?.find(p => value.startsWith(p));
        return prefix ? value.slice(prefix.length) : value;
      }),
    requirement: requirementSchema,
    pickup: addressSchema.refine(data => data.landmark.trim().length > 0, {
      message: FareEstimateFormErrorMsg.pickup,
    }),
    drop: addressSchema.refine(data => data.landmark.trim().length > 0, {
      message: FareEstimateFormErrorMsg.drop,
    }),
    consentForCallback: makeConsentSchema(consentConfig),
  });
};

export const schema = makeSchema(FormValidationRegex.mobile, {
  isRequired: true,
  shouldShow: true,
});

export type FormData = zod.infer<typeof schema>;
